import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save,
  CheckCircle,
  RefreshCw,
  Trash2,
  ArrowLeft,
  Loader2,
  Info,
} from 'lucide-react';
import {
  getFactura,
  updateFactura,
  validarFactura,
  reprocesarFactura,
  deleteFactura,
  getProveedores,
  getCategorias,
  getFacturaPdfUrl,
} from '../services/api';
import PDFViewer from '../components/PDFViewer';
import StatusBadge from '../components/StatusBadge';
import toast from 'react-hot-toast';

export default function FacturaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [factura, setFactura] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [categorias, setCategorias] = useState([]);

  useEffect(() => {
    Promise.all([
      getFactura(id),
      getProveedores().catch(() => []),
      getCategorias().catch(() => []),
    ])
      .then(([f, p, c]) => {
        setFactura(f);
        setForm({
          numero_factura: f.numero_factura || '',
          fecha_factura: f.fecha_factura || '',
          fecha_vencimiento: f.fecha_vencimiento || '',
          proveedor_id: f.proveedor_id || '',
          proveedor_nombre: f.proveedor_nombre || '',
          cif_proveedor: f.proveedor_cif || '',
          base_imponible: f.base_imponible ?? '',
          tipo_iva: f.tipo_iva ?? '',
          cuota_iva: f.cuota_iva ?? '',
          tipo_irpf: f.tipo_irpf ?? '',
          cuota_irpf: f.cuota_irpf ?? '',
          total: f.total ?? '',
          categoria: f.categoria || '',
          notas: f.notas || '',
          concepto: f.concepto || '',
        });
        setProveedores(Array.isArray(p) ? p : []);
        setCategorias(Array.isArray(c) ? c : []);
      })
      .catch((err) => toast.error('Error cargando factura: ' + err.message))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { ...form };
      // Convert numeric fields
      ['base_imponible', 'tipo_iva', 'cuota_iva', 'tipo_irpf', 'cuota_irpf', 'total'].forEach((f) => {
        if (data[f] !== '' && data[f] !== null && data[f] !== undefined) {
          data[f] = parseFloat(data[f]);
        } else {
          data[f] = null;
        }
      });
      if (data.proveedor_id === '') data.proveedor_id = null;
      // Remove read-only/joined fields not in FacturaUpdate model
      delete data.proveedor_nombre;
      delete data.cif_proveedor;
      delete data.concepto;
      delete data.fecha_vencimiento;
      const updated = await updateFactura(id, data);
      setFactura(updated);
      toast.success('Factura guardada');
    } catch (err) {
      toast.error('Error guardando: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleValidar = async () => {
    try {
      const updated = await validarFactura(id);
      setFactura(updated);
      toast.success('Factura validada');
    } catch (err) {
      toast.error('Error validando: ' + err.message);
    }
  };

  const handleReprocesar = async () => {
    try {
      const updated = await reprocesarFactura(id);
      setFactura(updated);
      toast.success('Reprocesamiento OCR iniciado');
    } catch (err) {
      toast.error('Error reprocesando: ' + err.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar esta factura?')) return;
    try {
      await deleteFactura(id);
      toast.success('Factura eliminada');
      navigate('/facturas');
    } catch (err) {
      toast.error('Error eliminando: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!factura) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Factura no encontrada</p>
        <button onClick={() => navigate('/facturas')} className="btn-primary mt-4">
          Volver a facturas
        </button>
      </div>
    );
  }

  const confidence = factura.ocr_confianza;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/facturas')}
            className="p-2 hover:bg-gray-100 rounded-lg shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
              Factura {factura.numero_factura || `#${id}`}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={factura.estado} />
              {confidence !== null && confidence !== undefined && (
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    confidence >= 0.8
                      ? 'bg-green-100 text-green-700'
                      : confidence >= 0.5
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                  }`}
                >
                  OCR: {Math.round(confidence * 100)}%
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <button onClick={handleReprocesar} className="btn-secondary flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap py-1.5 px-3">
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Reprocesar</span> OCR
          </button>
          <button onClick={handleValidar} className="btn-primary flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap py-1.5 px-3">
            <CheckCircle className="w-4 h-4" />
            Validar
          </button>
          <button onClick={handleDelete} className="btn-danger flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap py-1.5 px-3">
            <Trash2 className="w-4 h-4" />
            Eliminar
          </button>
        </div>
      </div>

      {/* Split view - form first on mobile for usability */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Form (shown first on mobile) */}
        <div className="card !p-4 sm:!p-6 order-1 lg:order-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase">Datos de la Factura</h2>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-1 text-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">N Factura</label>
                <input
                  type="text"
                  value={form.numero_factura}
                  onChange={(e) => handleChange('numero_factura', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Factura</label>
                <input
                  type="date"
                  value={form.fecha_factura}
                  onChange={(e) => handleChange('fecha_factura', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Fecha Vencimiento
                </label>
                <input
                  type="date"
                  value={form.fecha_vencimiento}
                  onChange={(e) => handleChange('fecha_vencimiento', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Proveedor</label>
                <select
                  value={form.proveedor_id}
                  onChange={(e) => handleChange('proveedor_id', e.target.value)}
                  className="input-field text-sm"
                >
                  <option value="">-- Seleccionar --</option>
                  {proveedores.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Nombre Proveedor
                </label>
                <input
                  type="text"
                  value={form.proveedor_nombre}
                  onChange={(e) => handleChange('proveedor_nombre', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">CIF Proveedor</label>
                <input
                  type="text"
                  value={form.cif_proveedor}
                  onChange={(e) => handleChange('cif_proveedor', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
            </div>

            <hr className="border-gray-200" />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Base Imponible
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.base_imponible}
                  onChange={(e) => handleChange('base_imponible', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo IVA (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.tipo_iva}
                  onChange={(e) => handleChange('tipo_iva', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cuota IVA</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.cuota_iva}
                  onChange={(e) => handleChange('cuota_iva', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo IRPF (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.tipo_irpf}
                  onChange={(e) => handleChange('tipo_irpf', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cuota IRPF</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.cuota_irpf}
                  onChange={(e) => handleChange('cuota_irpf', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Total</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.total}
                  onChange={(e) => handleChange('total', e.target.value)}
                  className="input-field text-sm font-semibold"
                />
              </div>
            </div>

            <hr className="border-gray-200" />

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Concepto</label>
              <input
                type="text"
                value={form.concepto}
                onChange={(e) => handleChange('concepto', e.target.value)}
                className="input-field text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Categoría</label>
              <select
                value={form.categoria}
                onChange={(e) => handleChange('categoria', e.target.value)}
                className="input-field text-sm"
              >
                <option value="">-- Sin categoría --</option>
                {categorias.map((c) => (
                  <option key={c.id || c.nombre} value={c.id || c.nombre}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notas</label>
              <textarea
                value={form.notas}
                onChange={(e) => handleChange('notas', e.target.value)}
                rows={3}
                className="input-field text-sm resize-none"
              />
            </div>

            {/* Info */}
            {factura.origen && (
              <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
                <Info className="w-3 h-3" />
                Origen: {factura.origen}
                {factura.created_at && (
                  <> | Registrada: {new Date(factura.created_at).toLocaleString('es-ES')}</>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Document viewer (shown second on mobile, first on desktop) */}
        <div className="card !p-4 order-2 lg:order-1">
          <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3">Documento</h2>
          <PDFViewer url={getFacturaPdfUrl(id)} filename={factura.ruta_archivo || factura.archivo_renombrado || factura.archivo_original} />
        </div>
      </div>
    </div>
  );
}
